import { useState, useEffect } from 'react'
import { Calendar, Trash2 } from 'lucide-react'
import { Button } from '../../../../../../ui'
import { useTransition, animated } from '@react-spring/web'
import { createPortal } from 'react-dom'
import X from '@mui/icons-material/Clear'

const MONEDAS = [
  { value: 'CLP', label: 'Peso Chileno (CLP)' },
  { value: 'UF', label: 'Unidad de Fomento (UF)' },
  { value: 'USD', label: 'Dólar (USD)' }
]

const calculateNextEmission = (fecha_inicio, frecuencia, dia_del_mes, dia_de_semana) => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  let anchorDate = new Date(today)

  if (fecha_inicio) {
    const parts = String(fecha_inicio).split('-')
    if (parts.length === 3) {
      const parsedDate = new Date(parts[0], parts[1] - 1, parts[2])
      if (parsedDate > anchorDate) anchorDate = parsedDate
    }
  }

  const applyFrequencyJump = (date, freq) => {
    if (freq === 'monthly') date.setMonth(date.getMonth() + 1)
    else if (freq === 'quarterly') date.setMonth(date.getMonth() + 3)
    else if (freq === 'semiannual' || freq === 'biannual') date.setMonth(date.getMonth() + 6)
    else if (freq === 'yearly') date.setFullYear(date.getFullYear() + 1)
  }

  let finalDate = new Date(anchorDate)

  if (['monthly', 'quarterly', 'semiannual', 'biannual', 'yearly'].includes(frecuencia)) {
    finalDate = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), dia_del_mes || 15)
    if (finalDate < anchorDate) {
      applyFrequencyJump(finalDate, frecuencia)
    }
  } else if (frecuencia === 'weekly') {
    let target = dia_de_semana === 7 ? 0 : (dia_de_semana || 1)
    let daysToAdd = target - finalDate.getDay()
    if (daysToAdd < 0) daysToAdd += 7
    finalDate.setDate(finalDate.getDate() + daysToAdd)
  }

  const dd = String(finalDate.getDate()).padStart(2, '0')
  const mm = String(finalDate.getMonth() + 1).padStart(2, '0')
  const yyyy = finalDate.getFullYear()
  return `${dd}-${mm}-${yyyy}`
}

export default function ModalEditarProgramado({ open, onClose, row, onSave }) {
  const [doc, setDoc] = useState(null)

  useEffect(() => {
    if (row) {
      setDoc(JSON.parse(JSON.stringify(row)))
    }
  }, [row])

  const transition = useTransition(open, {
    from: { y: '50px', opacity: 0 },
    enter: { y: '0px', opacity: 1 },
    leave: { y: '50px', opacity: 0 },
    config: { tension: 250, friction: 25, mass: 1 },
  })

  const modalRoot = typeof document !== 'undefined' ? document.getElementById('modal-root') || document.body : null

  if (!doc || !modalRoot) return null

  const handleItemChange = (index, field, value) => {
    const newDetails = [...doc.details]
    newDetails[index][field] = value
    setDoc({ ...doc, details: newDetails })
  }

  const handleAddItem = () => {
    const newItem = {
      item_name: '',
      item_description: '',
      quantity: 1,
      unit_price: 0,
      discount_percent: 0
    }
    setDoc({ ...doc, details: [...doc.details, newItem] })
  }

  const handleRemoveItem = index => {
    const newDetails = doc.details.filter((_, i) => i !== index)
    setDoc({ ...doc, details: newDetails })
  }

  const grandTotal = doc.details.reduce((acc, item) => acc + (Number(item.quantity) || 0) * (Number(item.unit_price) || 0), 0)

  const ivaRate = (String(doc.tipo_documento) === '33' && doc.currency === 'CLP') ? 0.19 : 0
  const ivaAmount = doc.currency === 'CLP' ? grandTotal * ivaRate : 0
  const finalTotal = grandTotal + ivaAmount

  return createPortal(
    transition(
      (style, item) =>
        item && (
          <animated.div
            style={{ opacity: style.opacity }}
            className='fixed inset-0 z-[1500] flex items-center justify-center bg-slate-950/40 backdrop-blur-[2px] p-4'
            onClick={onClose}
          >
            <animated.div
              style={{
                opacity: style.opacity,
                transform: style.y.to(y => `translateY(${y})`),
                width: '100%',
                maxWidth: 780,
              }}
              className='relative flex flex-col bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh]'
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className='flex items-center justify-between bg-primary px-5 py-0'>
                <div />
                <button
                  onClick={onClose}
                  className='text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-all my-1'
                >
                  <X style={{ fontSize: 20 }} />
                </button>
              </div>

              {/* Body */}
              <div className='flex-1 overflow-y-auto p-5 scroll-smooth custom-scrollbar'>

                <div className='flex flex-col mb-5 border-b border-slate-100 pb-5'>
                  <h2 className='text-lg font-bold text-slate-900 tracking-tight'>Editar Documento Programado</h2>
                  <p className='text-xs text-slate-500 mt-1 mb-2.5'>
                    Ajusta la frecuencia, fecha de emisión y detalle del documento.
                  </p>
                  <div className='inline-flex items-center self-start px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-md'>
                    <p className='text-[10px] text-slate-600 font-bold uppercase tracking-wider flex items-center gap-1.5'>
                      <span className='w-1.5 h-1.5 rounded-full bg-slate-400'></span>
                      {doc.business_name || 'SIN RAZÓN SOCIAL'} <span className='text-slate-300 font-normal'>|</span> RUT: {doc.rut_receptor || '—'}
                    </p>
                  </div>
                </div>

                <div className='mb-6'>
                  <h3 className='text-xs font-bold text-slate-800 uppercase tracking-widest mb-4'>Configuración de programación</h3>
                  <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-4'>
                    <div>
                      <label className='block text-[11px] font-bold text-slate-700 mb-1.5'>Frecuencia</label>
                      <select
                        value={doc.frecuencia || 'monthly'}
                        onChange={e => setDoc({ ...doc, frecuencia: e.target.value })}
                        className='w-full border border-slate-200 bg-slate-50 rounded-xl px-3 py-2 text-sm text-slate-700 outline-none focus:bg-white focus:border-blue-600/50 focus:ring-4 focus:ring-blue-600/10 transition-all shadow-sm'
                      >
                        <option value='daily'>Diario</option>
                        <option value='weekly'>Semanal</option>
                        <option value='monthly'>Mensual</option>
                        <option value='quarterly'>Trimestral</option>
                        <option value='biannual'>Semestral</option>
                        <option value='yearly'>Anual</option>
                      </select>
                    </div>
                    <div>
                      <label className='block text-[11px] font-bold text-slate-700 mb-1.5'>Moneda</label>
                      <select
                        value={doc.currency || 'CLP'}
                        onChange={e => setDoc({ ...doc, currency: e.target.value })}
                        className='w-full border border-slate-200 bg-slate-50 rounded-xl px-3 py-2 text-sm text-slate-700 outline-none focus:bg-white focus:border-blue-600/50 focus:ring-4 focus:ring-blue-600/10 transition-all shadow-sm'
                      >
                        {MONEDAS.map(m => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className='block text-[11px] font-bold text-slate-700 mb-1.5'>
                        {doc.frecuencia === 'weekly' ? 'Día de emisión (1-7)' : 'Día de emisión'}
                      </label>
                      <input
                        type='number'
                        value={doc.frecuencia === 'weekly' ? (doc.dia_de_semana || 1) : (doc.dia_del_mes || 15)}
                        onChange={e => {
                          if (doc.frecuencia === 'weekly') {
                            setDoc({ ...doc, dia_de_semana: Number(e.target.value) })
                          } else {
                            setDoc({ ...doc, dia_del_mes: Number(e.target.value) })
                          }
                        }}
                        className='w-full border border-slate-200 bg-slate-50 rounded-xl px-3 py-2 text-sm text-slate-700 outline-none focus:bg-white focus:border-blue-600/50 focus:ring-4 focus:ring-blue-600/10 transition-all shadow-sm'
                      />
                    </div>
                  </div>

                  <div className='flex justify-between items-center bg-[#EFF6FF] border border-blue-100 rounded-lg py-2 px-3 shadow-sm'>
                    <div className='flex items-center gap-2.5'>
                      <div className='h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center'>
                        <Calendar className='text-blue-600' size={14} />
                      </div>
                      <div>
                        <p className='text-[11px] font-bold text-blue-900'>Próxima emisión</p>
                        <p className='text-[10px] text-blue-700 leading-tight'>Según la frecuencia seleccionada</p>
                      </div>
                    </div>
                    <p className='text-sm font-semibold text-blue-800'>
                      {calculateNextEmission(doc.fecha_inicio, doc.frecuencia, doc.dia_del_mes, doc.dia_de_semana)}
                    </p>
                  </div>
                </div>

                <div className='mb-6 pt-2'>
                  <div className='flex items-center justify-between mb-4'>
                    <h3 className='text-xs font-bold text-slate-800 uppercase tracking-widest'>Detalle del documento</h3>
                    <button
                      onClick={handleAddItem}
                      className='flex text-m items-center gap-1.5 px-4 py-1 rounded-full text-white tracking-wide transition-all shadow-md bg-accent2'
                    >
                      <span className='text-[10px]'>+</span> Agregar ítem
                    </button>
                  </div>

                  <div className='space-y-4 max-h-[280px] overflow-y-auto pr-2 custom-scrollbar'>
                    {doc.details.map((item, index) => (
                      <div key={index} className='border border-slate-100 rounded-xl p-4 bg-white shadow-sm relative group'>
                        <div className='grid grid-cols-12 gap-4 items-end mb-4'>
                          <div className='col-span-6'>
                            <label className='block text-[11px] font-bold text-slate-700 mb-1.5'>Producto o servicio</label>
                            <input
                              type='text'
                              value={item.item_name}
                              onChange={e => handleItemChange(index, 'item_name', e.target.value)}
                              placeholder='Ej. Desarrollo web'
                              className='w-full border border-slate-200 bg-slate-50 rounded-xl px-3 py-2 text-sm text-slate-700 outline-none focus:bg-white focus:border-blue-600/50 focus:ring-4 focus:ring-blue-600/10 transition-all'
                            />
                          </div>
                          <div className='col-span-2'>
                            <label className='block text-[11px] font-bold text-slate-700 mb-1.5'>Cantidad</label>
                            <input
                              type='number'
                              value={item.quantity}
                              onChange={e => handleItemChange(index, 'quantity', Number(e.target.value))}
                              className='w-full border border-slate-200 bg-slate-50 rounded-xl px-3 py-2 text-sm text-slate-700 outline-none focus:bg-white focus:border-blue-600/50 focus:ring-4 focus:ring-blue-600/10 transition-all'
                            />
                          </div>
                          <div className='col-span-2'>
                            <label className='block text-[11px] font-bold text-slate-700 mb-1.5'>Precio unit.</label>
                            <div className="relative">
                              {doc.currency !== 'UF' && doc.currency !== 'USD' && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>}
                              <input
                                type='number'
                                value={item.unit_price}
                                disabled={['UF', 'USD'].includes(doc.currency)}
                                onChange={e => handleItemChange(index, 'unit_price', Number(e.target.value))}
                                className={`w-full border border-slate-200 bg-slate-50 rounded-xl py-2 text-sm text-slate-700 outline-none focus:bg-white focus:border-blue-600/50 focus:ring-4 focus:ring-blue-600/10 transition-all ${['UF', 'USD'].includes(doc.currency) ? 'px-3 disabled:text-slate-400' : 'pl-6 pr-3'}`}
                              />
                            </div>
                          </div>
                          <div className='col-span-2 flex flex-col'>
                            <label className='block text-[11px] font-bold text-slate-500 mb-1.5'>Total línea</label>
                            <div className='flex justify-between items-center h-[38px]'>
                              <span className='text-sm text-slate-800 font-bold'>
                                {doc.currency === 'UF' || doc.currency === 'USD' ? '' : '$'}
                                {Number(item.quantity * item.unit_price).toLocaleString('es-CL', { maximumFractionDigits: 2 })}
                              </span>


                              <button
                                onClick={() => handleRemoveItem(index)}
                                className='text-rose-600 hover:bg-rose-50 p-1.5 rounded-lg transition-all'
                                title="Eliminar línea"
                              >
                                <Trash2 size={16} />
                              </button>

                            </div>
                          </div>
                        </div>
                        <div>
                          <label className='block text-[11px] font-bold text-slate-700 mb-1.5'>Descripción adicional (opcional)</label>
                          <textarea
                            value={item.item_description || ''}
                            onChange={e => handleItemChange(index, 'item_description', e.target.value)}
                            rows={2}
                            placeholder='Añade más detalles si es necesario...'
                            className='w-full border border-slate-200 bg-slate-50 rounded-xl px-3 py-2 text-sm text-slate-700 outline-none focus:bg-white focus:border-blue-600/50 focus:ring-4 focus:ring-blue-600/10 transition-all resize-none'
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className='text-xs font-bold text-slate-800 uppercase tracking-widest mb-3'>Resumen del documento</h3>
                  <div className='bg-slate-50/50 border border-slate-200/50 rounded-2xl p-4 flex flex-col md:flex-row md:items-end justify-between items-start gap-4'>
                    <div className='flex flex-col text-xs text-slate-500 gap-1.5'>
                      <div className='flex items-center gap-2'>
                        <span className='font-medium w-20'>Subtotal</span>
                        <span className='text-slate-800 font-semibold'>
                          {doc.currency === 'UF' || doc.currency === 'USD' ? '' : '$'}
                          {grandTotal.toLocaleString('es-CL', { maximumFractionDigits: 0 })}
                        </span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <span className='font-medium w-20'>IVA {ivaRate > 0 ? '(19%)' : ''}</span>
                        <span className='text-slate-800 font-semibold'>
                          {doc.currency === 'UF' || doc.currency === 'USD' ? '' : '$'}
                          {ivaAmount.toLocaleString('es-CL', { maximumFractionDigits: 0 })}
                        </span>
                      </div>
                    </div>

                    <div className='bg-white px-5 py-2.5 border border-slate-200 rounded-xl shadow-sm text-right min-w-[200px]'>
                      <span className='block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-0.5'>Monto Total</span>
                      <span className='text-2xl font-bold text-slate-900 tracking-tight'>
                        {doc.currency === 'UF' || doc.currency === 'USD' ? '' : '$'}
                        {finalTotal.toLocaleString('es-CL', { maximumFractionDigits: 0 })}
                        {doc.currency === 'UF' ? ' UF' : doc.currency === 'USD' ? ' USD' : ''}
                      </span>
                    </div>
                  </div>
                </div>

              </div> {/* Cierre del Body */}

              {/* Footer */}
              <div className='border-t border-slate-100 px-6 py-4 flex justify-end bg-slate-50/30'>
                <div className='flex items-center gap-3 px-2'>
                  <Button
                    onClick={onClose}
                    className='h-9 px-6 text-[12px] font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors border-none shadow-none rounded-lg'
                    title="Cerrar"
                  />
                  <Button
                    onClick={() => onSave(doc)}
                    className='h-9 px-6 text-[12px] font-semibold bg-primary text-white hover:opacity-90 transition-all shadow-sm shadow-primary/20 rounded-lg'
                    title="Guardar Cambios"
                  />
                </div>
              </div>

            </animated.div>

            <style>{`
              .custom-scrollbar::-webkit-scrollbar {
                width: 6px;
              }
              .custom-scrollbar::-webkit-scrollbar-track {
                background: transparent;
              }
              .custom-scrollbar::-webkit-scrollbar-thumb {
                background: #e2e8f0;
                border-radius: 10px;
              }
              .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                background: #cbd5e1;
              }
            `}</style>
          </animated.div>
        )
    ),
    modalRoot
  )
}
